import { useEffect, useState } from "react";
import { MerchantSubscription, ShopSubscription, activateShopSubscription, addShopDeliveryTranche, suspendShopSubscription } from "../redux/actions/subscriptions";
import { Box, CircularProgress, MenuItem, Typography } from "@material-ui/core";
import ThreeDotMenu from "./ThreeDotMenu";
import { theme } from "../constants/theme";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import CreditCard from '@material-ui/icons/CreditCard';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import TodayIcon from '@material-ui/icons/Today';
import SpeedIcon from '@material-ui/icons/Speed';
import RedeemIcon from '@material-ui/icons/Redeem';
import { SubscriptionAddTrancheModal } from "./SubscriptionAddTrancheModal";
import { SubscriptionSuspendModal } from "./SubscriptionSuspendModal";
import { SubscriptionActivateShopModal } from "./SubscriptionActivateShopModal";
import { SubscriptionCardModal } from "../utils/subscriptions";
import { SubscriptionUpdatePremiumModal } from "./SubscriptionUpdatePremiumModal";

interface ShopProps {
  classes: any;
  shop: ShopSubscription;
  merchant: MerchantSubscription;
  isAdmin: boolean;
}
export default function SubscriptionCardShop({ classes, shop, merchant, isAdmin }: ShopProps) {
  const [visibleModal, setVisibleModal] = useState<SubscriptionCardModal>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop]);

  function onCloseModal() {
    setVisibleModal(undefined);
  }

  const onActivate = () => {
    setLoading(true);
    activateShopSubscription(
      { ShopId: shop.ShopId, MerchantId: merchant.MerchantId },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onSuspend = () => {
    setLoading(true);
    suspendShopSubscription(
      { ShopId: shop.ShopId },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const onAddDeliveryTranche = (numberOfCredits: number, price: number) => {
    setLoading(true);
    addShopDeliveryTranche(
      { ShopId: shop.ShopId, NumberOfCredits: numberOfCredits, Price: Math.round(price * 100) },
      () => { onCloseModal(); },
      () => { }
    );
  };

  const getSubscriptionDateText = () => {
    if (shop.SubscriptionStartDate && shop.SubscriptionExpiryDate) {
      return `${new Date(shop.SubscriptionStartDate).toLocaleDateString()} - ${new Date(shop.SubscriptionExpiryDate).toLocaleDateString()}`;
    }

    if (shop.SubscriptionStartDate) {
      new Date(shop.SubscriptionStartDate).toLocaleDateString()
    }
  }

  const hasTranche = shop && shop.TrancheCreditsRemaining && shop.TrancheCreditsRemaining > 0;
  return (
    <Box className={classes.storeContainer}>
      <Box>
        <Typography variant="h6" component="h2">
          {shop.ShopName}
        </Typography>
        {shop.HasCredits &&
          <Box style={{ display: 'flex', flexDirection: 'column', marginTop: 16 }}>
            <Box style={{ display: 'flex', gap: 28 }}>
              {shop.HasActiveShopSubscription &&
                <Box>
                  <Typography variant="overline">Credits</Typography>
                  <Typography variant="body2" className={classes.creditLeft}>
                    <SpeedIcon />
                    {shop.SubscriptionCreditsRemaining} credits
                  </Typography>
                </Box>
              }
              {hasTranche ?
                <Box>
                  <Typography variant="overline">Tranches</Typography>
                  <Typography variant="body2" className={classes.creditLeft}>
                    <RedeemIcon />
                    {shop.TrancheCreditsRemaining} credits
                  </Typography>
                </Box>
                : null
              }
              {shop.SubscriptionStartDate ?
                <>
                  <Box>
                    <Typography variant="overline">Subscription Date</Typography>
                    <Typography variant="body2" className={classes.creditLeft}>
                      <TodayIcon />
                      {getSubscriptionDateText()}
                    </Typography>
                  </Box>
                </>
                : null}
            </Box>
          </Box>
        }
      </Box>
      <Box className={classes.storeInfoContainer}>
        <Typography
          data-automation="promotion-code"
          variant="body2"
          className={shop.HasActiveShopSubscription ? classes.active : classes.suspended}
          component="span">
          {loading ? <CircularProgress size={24} /> : shop.HasActiveShopSubscription ? 'Active' : 'Inactive'}
        </Typography>

        {isAdmin &&
          <ThreeDotMenu>
            {shop.HasActiveShopSubscription
              ?
              <MenuItem
                style={{ color: theme.palette.error.main }}
                onClick={() => setVisibleModal(SubscriptionCardModal.SUSPEND)}
              >
                <DeleteIcon />
                <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                  Suspend subscription
                </Typography>
              </MenuItem>
              : <MenuItem
                style={{ color: theme.palette.success.main }}
                onClick={() => setVisibleModal(SubscriptionCardModal.ACTIVATE)}
              >
                <CheckCircleIcon />
                <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                  Activate subscription
                </Typography>
              </MenuItem>}
            <MenuItem
              onClick={() => setVisibleModal(SubscriptionCardModal.ADD_DELIVERY_TRANCHE)}
            >
              <CreditCard />
              <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                Add delivery tranche
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => setVisibleModal(SubscriptionCardModal.UPDATE_PREMIUM_PREFERENCES)}
            >
              <ToggleOnIcon />
              <Typography variant="body2" component="p" style={{ paddingLeft: '16px' }}>
                Update concierge preferences
              </Typography>
            </MenuItem>
          </ThreeDotMenu>
        }
      </Box>

      {<SubscriptionAddTrancheModal isVisible={visibleModal === SubscriptionCardModal.ADD_DELIVERY_TRANCHE} close={onCloseModal} addTranche={onAddDeliveryTranche} />}
      {<SubscriptionSuspendModal isVisible={visibleModal === SubscriptionCardModal.SUSPEND} close={onCloseModal} suspend={onSuspend} />}
      {<SubscriptionActivateShopModal isVisible={visibleModal === SubscriptionCardModal.ACTIVATE} close={onCloseModal} activate={onActivate} />}
      {
        <SubscriptionUpdatePremiumModal
          close={onCloseModal}
          shopId={shop.ShopId}
          premiumEnabled={shop.PremiumEnabled}
          premiumOnly={shop.PremiumOnly}
          isVisible={visibleModal === SubscriptionCardModal.UPDATE_PREMIUM_PREFERENCES}
        />
      }
    </Box>
  );
};